import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { api, formatDate, timeAgo, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const STATUS = {
  pending_acast: { label: "Queued", cls: "bg-amber-400/15 text-amber-300", Icon: Clock },
  scheduled: { label: "Scheduled", cls: "bg-sky-400/15 text-sky-300", Icon: Clock },
  published: { label: "Published", cls: "bg-emerald-400/15 text-emerald-300", Icon: CheckCircle2 },
  failed: { label: "Needs attention", cls: "bg-rose-500/15 text-rose-400", Icon: AlertTriangle },
  rejected: { label: "Not published", cls: "bg-white/10 text-slate-400", Icon: AlertTriangle },
};
const FILTERS = [["all", "All"], ["published", "Published"], ["scheduled", "Scheduled"], ["pending_acast", "Queued"], ["failed", "Needs attention"]];
const fmtWhen = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York", timeZoneName: "short" });

export default function MySubmissionsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subs, setSubs] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { if (user) api.get("/portal/submissions").then((r) => setSubs(r.data)).catch(() => setSubs([])); }, [user]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;
  if (!(user.role === "podcaster" || (user.role === "admin" && user.team_id))) return <Navigate to="/admin" replace />;

  const visible = (subs || []).filter((s) => filter === "all" || s.status === filter);
  return (
    <div className="min-h-screen" data-testid="my-submissions-page">
      <header className="border-b border-white/10 bg-[#050B17]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3"><Logo size={36} /><span className="font-display font-bold text-lg">Your submissions</span></Link>
          <Link to="/publish" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="my-submissions-back"><ArrowLeft size={15} /> Back to publishing</Link>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Episode history</p>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-black tracking-tight">Everything you've submitted</h1>
            <p className="mt-2 text-sm text-slate-400">Click any episode to edit its title, summary, artwork or schedule. Changes sync to Acast automatically.</p>
          </div>
          <div className="inline-flex flex-wrap rounded-full bg-white/5 p-1" data-testid="my-submissions-filter">
            {FILTERS.map(([k, label]) => <button key={k} type="button" onClick={() => setFilter(k)} className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${filter === k ? "bg-sky-400 text-[#070F1E]" : "text-slate-300 hover:text-white"}`} data-testid={`my-submissions-filter-${k}`}>{label}</button>)}
          </div>
        </div>

        {subs === null && <p className="mt-10 text-sm text-slate-400">Loading…</p>}
        {subs && visible.length === 0 && <p className="mt-10 text-sm text-slate-400" data-testid="my-submissions-empty">Nothing here yet.</p>}
        <div className="mt-8 space-y-3">
          {visible.map((s, i) => {
            const st = STATUS[s.status] || STATUS.pending_acast;
            const editable = s.status !== "rejected";
            return (
              <article key={s.id} className="card-surface rounded-2xl p-4 sm:p-5 flex gap-4 items-start" data-testid={`my-submission-${i}`}>
                {s.image_url ? <img src={imgUrl(s.image_url)} alt="" className="h-16 w-16 rounded-xl object-cover shrink-0" /> : <div className="h-16 w-16 rounded-xl bg-[#053671] shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-semibold text-base truncate" data-testid={`my-submission-title-${i}`}>{s.title}</h2>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${st.cls}`} data-testid={`my-submission-status-${i}`}><st.Icon size={11} /> {st.label}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-400 truncate">{s.show_name} · submitted {timeAgo(s.created_at)} · {s.audio_filename}</p>
                  <p className="mt-1 text-xs text-slate-300">{s.publish_at ? `${new Date(s.publish_at) > new Date() ? "Publishes" : "Published"} ${fmtWhen(s.publish_at)}` : s.published_at ? `Published ${fmtWhen(s.published_at)}` : `Submitted ${formatDate(s.created_at)}`}</p>
                  {s.error && <p className="mt-1 text-xs text-rose-300">{s.error}</p>}
                  <p className="mt-2 text-xs text-slate-500 line-clamp-2">{s.description}</p>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  {editable && <button type="button" onClick={() => navigate(`/publish?edit=${s.id}`)} className="inline-flex items-center gap-1.5 rounded-full bg-sky-400 px-4 py-2 text-xs font-bold text-[#070F1E] hover:bg-sky-300" data-testid={`my-submission-edit-${i}`}><Pencil size={13} /> Edit</button>}
                </div>
              </article>
            );
          })}
        </div>
      </main>
    </div>
  );
}
